import React, { useState, useEffect } from 'react';
import {
  Drawer, Box, Typography, IconButton, Avatar, Chip, CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import validationService from '../../services/validationService';

const actionConfig = {
  validated_approved: { label: 'Approved', color: '#2e7d32', bgColor: '#e8f5e9', borderColor: '#2e7d32', icon: '✓' },
  validated_changes_requested: { label: 'Changes requested', color: '#d32f2f', bgColor: '#ffebee', borderColor: '#d32f2f', icon: '!' },
  validation_reset: { label: 'Validation reset', color: '#f57f17', bgColor: '#fff8e1', borderColor: '#f9a825', icon: '↻' },
  validator_assigned: { label: 'Validator assigned', color: '#475969', bgColor: '#f5f5f5', borderColor: '#9e9e9e', icon: '+' },
  validator_removed: { label: 'Validator removed', color: '#475969', bgColor: '#f5f5f5', borderColor: '#9e9e9e', icon: '−' },
  item_validated: { label: 'Item validated', color: '#1976d2', bgColor: '#e3f2fd', borderColor: '#1976d2', icon: '✓' },
  item_unvalidated: { label: 'Item unvalidated', color: '#f57f17', bgColor: '#fff8e1', borderColor: '#f9a825', icon: '✗' },
  content_added: { label: 'Content added', color: '#2e7d32', bgColor: '#e8f5e9', borderColor: '#4caf50', icon: '+' },
  content_edited: { label: 'Content edited', color: '#1565c0', bgColor: '#e3f2fd', borderColor: '#1976d2', icon: '✎' },
  content_deleted: { label: 'Content deleted', color: '#d32f2f', bgColor: '#ffebee', borderColor: '#d32f2f', icon: '−' },
};

const getTimeAgo = (dateStr) => {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} min${mins > 1 ? 's' : ''} ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} day${days > 1 ? 's' : ''} ago`;
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true
  });
};

const ValidationHistoryPanel = ({ open, onClose, projectId, taskId, restrictTo, currentUserMemberType, fetchKey }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTaskId, setActiveTaskId] = useState(null);

  const taskName = (activeTaskId || taskId || '').replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  useEffect(() => {
    if (open && projectId && taskId) {
      setActiveTaskId(taskId);
      fetchHistoryFor(projectId, taskId);
    }
  }, [open, fetchKey]);

  const fetchHistoryFor = async (pid, tid) => {
    setLoading(true);
    try {
      const data = await validationService.getValidationHistory(pid, tid);
      setHistory(data || []);
    } catch (err) {
      setHistory([]);
    }
    setLoading(false);
  };

  // Filter history based on restrictTo prop
  // 'both' = show all, 'member-type' = filter by current user's member type
  const effectiveFilter = restrictTo === 'member-type' ? (currentUserMemberType || 'internal') : restrictTo;
  const filteredHistory = effectiveFilter && effectiveFilter !== 'both'
    ? history.filter(entry => {
        // Use validator_type if available, otherwise infer from comment
        let type = entry.validator_type;
        if (!type && entry.comment) {
          const commentLower = entry.comment.toLowerCase();
          if (commentLower.includes('internal')) type = 'internal';
          else if (commentLower.includes('external')) type = 'external';
        }
        // If type still unknown, hide it
        if (!type) return false;
        return type === effectiveFilter;
      })
    : history;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: 480, bgcolor: '#fafafa', display: 'flex', flexDirection: 'column', top: '70px', height: 'calc(100% - 70px)' } }}
    >
      {/* Header - fixed at top */}
      <Box sx={{
        p: 2.5, px: 3,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        bgcolor: '#fff', borderBottom: '1px solid #e5e5e5',
        flexShrink: 0, position: 'sticky', top: 0, zIndex: 1
      }}>
        <Box>
          <Typography sx={{
            fontSize: 16, fontWeight: 700,
            fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif",
            color: '#475969'
          }}>
            Activity history
          </Typography>
          <Typography sx={{ fontSize: 13, color: '#72849a', mt: 0.25 }}>
            {taskName}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: '#475969' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Content */}
      <Box sx={{ p: 3, overflow: 'auto', flex: 1 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={28} sx={{ color: '#475969' }} />
          </Box>
        ) : filteredHistory.length === 0 ? (
          <Box sx={{
            textAlign: 'center', py: 6,
            bgcolor: '#fff', borderRadius: 2, border: '1px solid #e5e5e5'
          }}>
            <Typography sx={{ fontSize: 14, color: '#72849a' }}>
              No validation history yet
            </Typography>
            <Typography sx={{ fontSize: 12, color: '#a0aec0', mt: 0.5 }}>
              History will appear here when validators take actions
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {filteredHistory.map((entry, idx) => {
              const config = actionConfig[entry.action] || actionConfig.validator_assigned;
              const personName = entry.performed_by?.name || 'Unknown';
              const initial = personName.charAt(0).toUpperCase();

              return (
                <Box
                  key={entry.id || idx}
                  sx={{
                    bgcolor: '#fff',
                    borderRadius: 2,
                    border: '1px solid #e5e5e5',
                    borderLeft: `3px solid ${config.borderColor}`,
                    p: 2,
                    transition: 'box-shadow 0.15s',
                    '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }
                  }}
                >
                  {/* Top row: avatar + name + badge + time */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar sx={{
                      width: 32, height: 32, fontSize: 13, fontWeight: 600,
                      bgcolor: config.bgColor, color: config.color
                    }}>
                      {initial}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#475969' }}>
                          {personName}
                        </Typography>
                        <Chip
                          label={config.label}
                          size="small"
                          sx={{
                            height: 20, fontSize: 11, fontWeight: 500,
                            bgcolor: config.bgColor, color: config.color,
                            border: 'none'
                          }}
                        />
                      </Box>
                      <Typography sx={{ fontSize: 11, color: '#a0aec0', mt: 0.25 }}>
                        {formatDate(entry.created_at)} · {getTimeAgo(entry.created_at)}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Comment */}
                  {entry.comment && (
                    <Box sx={{
                      mt: 1.5, ml: 5.5,
                      p: 1.5,
                      bgcolor: '#f8f9fa',
                      borderRadius: 1.5,
                      borderLeft: '2px solid #e2e8f0'
                    }}>
                      <Typography sx={{ fontSize: 12, color: '#4a5568', lineHeight: 1.5, fontStyle: 'italic' }}>
                        "{entry.comment}"
                      </Typography>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default ValidationHistoryPanel;
